import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { apiService } from "../services/api";
import { AuthService } from "../services/auth";
import { API_CONFIG } from "../config/constants";
import { Video, ApiResponse, ApiError } from "../types/api";

interface UseVideosReturn {
  videos: Video[];
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

export function useVideos(): UseVideosReturn {
  const auth = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchVideos = useCallback(async () => {
    if (!AuthService.isAuthenticated(auth)) {
      setError({ message: "User is not authenticated" });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const idToken = AuthService.getIdToken(auth);
      const response = await apiService.get<ApiResponse<Video[]>>(
        API_CONFIG.endpoints.videos,
        idToken,
      );

      setVideos(response.results || []);
    } catch (err) {
      const apiError: ApiError = {
        message: err instanceof Error ? err.message : "Failed to fetch videos",
        status: (err as ApiError)?.status,
        code: (err as ApiError)?.code,
      };
      setError(apiError);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  return {
    videos,
    isLoading,
    error,
    refetch: fetchVideos,
  };
}
